import {
  createContext,
  useContext,
  useState,
  useEffect,
  type FC,
  type PropsWithChildren,
} from 'react';

import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import type { EmployerSiteNames as EmployerSiteName } from 'src/siteConfig';

type NumberString = `${number}`;
type NonEmptyNumberStringArray = [NumberString, ...NumberString[]];

interface Feature<T> {
  name: T;
  gradualRolloutConfig?: {
    [K in EmployerSiteName]?: NonEmptyNumberStringArray;
  };
}

interface ProviderState<T> {
  featuresEnabledByQueryParams: Array<Feature<T>>;
  supportedFeatures: Array<Feature<T>>;
  advertiserSeekId: string | null;
  site: EmployerSiteName | null;
}

export const createFeatureContext = <T extends string>(
  supportedFeatures: Array<Feature<T>>,
) => {
  const createDefaultFeatureState = (): ProviderState<T> => ({
    featuresEnabledByQueryParams: [],
    supportedFeatures,
    advertiserSeekId: null,
    site: null,
  });

  const FeatureContext = createContext<ProviderState<T>>(
    createDefaultFeatureState(),
  );

  const useFeatureConfig = () => {
    const context = useContext(FeatureContext);

    if (!context) {
      throw new Error(
        'useFeatureConfig must be used within a FeatureConfigProvider',
      );
    }

    const isFeatureEnabled = (featureName: T): boolean => {
      if (context.site === null || context.advertiserSeekId === null) {
        return false;
      }

      if (
        context.featuresEnabledByQueryParams.some(
          (supportedFeature) => supportedFeature.name === featureName,
        )
      ) {
        return true;
      }

      return (
        context.supportedFeatures
          .find((supportedFeature) => supportedFeature.name === featureName)
          ?.gradualRolloutConfig?.[context.site]?.some((digit) =>
            context.advertiserSeekId?.endsWith(digit),
          ) ?? false
      );
    };

    return { isFeatureEnabled };
  };

  const FeatureConfigProvider: FC<
    PropsWithChildren<{
      advertiserSeekId: string | null;
      site: EmployerSiteName;
    }>
  > = ({ children, advertiserSeekId, site }) => {
    const [providerState, setProviderState] = useState<ProviderState<T>>(
      createDefaultFeatureState(),
    );

    const [featuresQueryParam] = useQueryParam(
      'features',
      withDefault(StringParam, ''),
    );

    useEffect(() => {
      const featuresArray = featuresQueryParam.split(',');
      const featuresEnabledByQueryParams = supportedFeatures.filter(
        ({ name }) => featuresArray.includes(name),
      );

      setProviderState({
        supportedFeatures,
        featuresEnabledByQueryParams,
        advertiserSeekId,
        site,
      });
    }, [advertiserSeekId, featuresQueryParam, site]);

    return (
      <FeatureContext.Provider value={providerState}>
        {children}
      </FeatureContext.Provider>
    );
  };

  return {
    useFeatureConfig,
    FeatureConfigProvider,
  };
};
